<template>
  <div class="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 w-72 shrink-0">
    <!-- Logo Section -->
    <div class="flex items-center justify-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <img src="/src/assests/TMS logo 1.svg" alt="TMS Logo" class="h-8 w-auto" />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>

    <!-- Navigation Links -->
    <nav v-else class="flex-1 min-h-0 px-4 py-4 space-y-4 overflow-y-auto">
      <!-- Categorized Menu Items -->
      <template v-for="category in categorizedMenuItems" :key="category.name">
        <!-- Category Header -->
        <div v-if="category.items.length > 0" class="space-y-2">
          <div class="px-3 py-1.5">
            <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ category.name }}
            </h3>
          </div>
          
          <!-- Menu Items in Category -->
          <div class="space-y-1">
            <template v-for="item in category.items" :key="item.id">
              <!-- Parent Menu Item -->
              <div class="space-y-1">
                <div
                  class="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 cursor-pointer"
                  :class="[
                    item.path && item.path.trim() && isActiveRoute(item.path)
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  ]"
                  @click="handleMenuItemClick(item)"
                >
                  <IconWithNotificationCount
                    class="mr-3 flex-shrink-0"
                    :count="getMenuItemBadgeCount(item)"
                    :title="getMenuItemBadgeTitle(item)"
                  >
                    <svg class="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getIconPath(resolveMenuIcon(item))"></path>
                    </svg>
                  </IconWithNotificationCount>
                  <span class="flex-1 leading-snug">{{ displayMenuLabel(item) }}</span>
                  <!-- Expand/Collapse Icon -->
                  <svg 
                    v-if="item.children && item.children.length > 0"
                    class="w-4 h-4 transition-transform duration-200 flex-shrink-0 ml-2"
                    :class="{ 
                      'rotate-90': isExpanded(item.id)
                    }"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>

                <!-- Child Menu Items -->
                <transition name="slide-down">
                  <div v-if="item.children && item.children.length > 0 && isExpanded(item.id)" class="ml-6 space-y-1">
                    <template v-for="child in item.children" :key="child.id">
                      <router-link
                        v-if="normalizeMenuPath(child.path)"
                        :to="normalizeMenuPath(child.path) as string"
                        class="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
                        :class="[
                          isActiveRoute(child.path)
                            ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300' 
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                        ]"
                      >
                        <svg class="w-4 h-4 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getIconPath(resolveMenuIcon(child))"></path>
                        </svg>
                        <span class="leading-snug">{{ displayMenuLabel(child) }}</span>
                      </router-link>
                      <div
                        v-else
                        class="flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      >
                        <svg class="w-4 h-4 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getIconPath(resolveMenuIcon(child))"></path>
                        </svg>
                        <span class="leading-snug">{{ displayMenuLabel(child) }}</span>
                      </div>
                    </template>
                  </div>
                </transition>
              </div>
            </template>
          </div>
        </div>
      </template>
    </nav>

    <!-- User Info and Logout -->
    <div class="shrink-0 px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div class="flex items-center mb-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors duration-200" @click="goToProfile">
        <div class="flex-shrink-0">
          <div class="w-10 h-10 rounded-full flex items-center justify-center shadow-sm overflow-hidden"
               :class="userProfilePicture ? 'bg-white dark:bg-gray-700' : 'bg-gradient-to-br from-primary-500 to-primary-600'">
            <img 
              v-if="userProfilePicture"
              :src="getProfilePictureUrl(userProfilePicture)"
              :alt="currentUser?.username || 'User'"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <span v-else class="text-sm font-semibold text-white">{{ currentUser?.username?.charAt(0).toUpperCase() }}</span>
          </div>
        </div>
        <div class="ml-3 flex-1 min-w-0">
          <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">Welcome</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 capitalize mt-1">{{ currentUser?.username }}</p>
        </div>
        <div class="flex-shrink-0 ml-2">
          <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </div>
      </div>
      <button
        @click="handleLogout"
        class="w-full min-h-[44px] inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200 dark:hover:bg-red-900/30"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
        </svg>
        Logout
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, onUnmounted } from 'vue'
import { useTokenRefreshReload } from '../composables/useTokenRefreshReload'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useMenu } from '../composables/useMenu'
import { simpleProfileApi } from '../services/api/teachers/simpleProfile'
import api, { teachersApi, postingHistoryApi } from '../services/api'
import { transferApplicationsApi, transferOrdersApi } from '../services/api/transfers'
import { joiningReportsApi } from '../services/api/reports/joiningReports'
import { priorityBadgesApi } from '../services/api/workflows'
import { grievancesApi } from '../services/api/grievances'
import { schoolsApi } from '../services/api/schools'
import { hardDeleteRequestsApi } from '../services/api/hardDeleteRequests'
import { fetchPendingMedicalRecordsCountForRole } from '../utils/pendingMedicalRecordsCount'
import { EDUCATION_QUALIFICATIONS_CHANGED_EVENT } from '../utils/educationQualificationsUiEvents'
import { isBranchAdminRole, canApproveEducationQualificationRole } from '../constants/roles'
import logger from '../utils/logger'
import { resolveSidebarMenuLabel } from '../utils/sidebarMenuLabel'
import { MEDICAL_RECORDS_CHANGED_EVENT } from '../utils/medicalRecordsUiEvents'
import IconWithNotificationCount from './IconWithNotificationCount.vue'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const currentUser = computed(() => auth.currentUser)
const userProfilePicture = ref<string | null>(null)

/** Wrapper so the template always has a stable `displayMenuLabel` (avoids HMR/instance issues). */
function displayMenuLabel(item: { label?: string; path?: string | null }): string {
  return resolveSidebarMenuLabel(item, currentUser.value?.role)
}

// Menu management
const { 
  filteredMenuItems,
  loading,
  loadMenuItems
} = useMenu()

// Expand/collapse state for menu items
const expandedItems = ref<Set<number>>(new Set())

// Pending transfer applications count
const pendingTransferCount = ref(0)
let pendingCountInterval: ReturnType<typeof setInterval> | null = null

// Pending joining reports count
const pendingJoiningReportsCount = ref(0)
let joiningReportsCountInterval: ReturnType<typeof setInterval> | null = null

// Pending priority badge approvals count
const pendingPriorityBadgeCount = ref(0)
let priorityBadgeCountInterval: ReturnType<typeof setInterval> | null = null

// Pending grievances count
const pendingGrievancesCount = ref(0)
let grievancesCountInterval: ReturnType<typeof setInterval> | null = null

// Pending hard delete requests count (super_admin only)
const pendingHardDeleteCount = ref(0)
let hardDeleteCountInterval: ReturnType<typeof setInterval> | null = null

/** Teacher: own pending submission(s); admins: pending in jurisdiction (list API). */
const pendingMedicalRecordsCount = ref(0)
let pendingMedicalRecordsInterval: ReturnType<typeof setInterval> | null = null

/** Education qualifications awaiting admin approval (pending, not rejected). */
const pendingEducationQualReviewCount = ref(0)
let pendingEducationQualReviewInterval: ReturnType<typeof setInterval> | null = null

/** Profile update requests awaiting admin review. */
const pendingProfileApprovalsCount = ref(0)
let pendingProfileApprovalsInterval: ReturnType<typeof setInterval> | null = null

/** Phone number change requests awaiting admin review. */
const pendingPhoneChangeRequestsCount = ref(0)
let pendingPhoneChangeRequestsInterval: ReturnType<typeof setInterval> | null = null

/** School info edit requests awaiting admin review. */
const pendingSchoolUpdateRequestsCount = ref(0)
let pendingSchoolUpdateRequestsInterval: ReturnType<typeof setInterval> | null = null

/** Minister-approved applications waiting for transfer order generation (admin roles). */
const pendingTransferOrderGenerationCount = ref(0)
let pendingTransferOrderGenerationInterval: ReturnType<typeof setInterval> | null = null

const isUnauthorized = (e: any) => Number(e?.response?.status) === 401
const stopSidebarPolling = () => {
  if (pendingCountInterval) {
    clearInterval(pendingCountInterval)
    pendingCountInterval = null
  }
  if (joiningReportsCountInterval) {
    clearInterval(joiningReportsCountInterval)
    joiningReportsCountInterval = null
  }
  if (priorityBadgeCountInterval) {
    clearInterval(priorityBadgeCountInterval)
    priorityBadgeCountInterval = null
  }
  if (grievancesCountInterval) {
    clearInterval(grievancesCountInterval)
    grievancesCountInterval = null
  }
  if (hardDeleteCountInterval) {
    clearInterval(hardDeleteCountInterval)
    hardDeleteCountInterval = null
  }
  if (pendingMedicalRecordsInterval) {
    clearInterval(pendingMedicalRecordsInterval)
    pendingMedicalRecordsInterval = null
  }
  if (pendingEducationQualReviewInterval) {
    clearInterval(pendingEducationQualReviewInterval)
    pendingEducationQualReviewInterval = null
  }
  if (pendingProfileApprovalsInterval) {
    clearInterval(pendingProfileApprovalsInterval)
    pendingProfileApprovalsInterval = null
  }
  if (pendingPhoneChangeRequestsInterval) {
    clearInterval(pendingPhoneChangeRequestsInterval)
    pendingPhoneChangeRequestsInterval = null
  }
  if (pendingSchoolUpdateRequestsInterval) {
    clearInterval(pendingSchoolUpdateRequestsInterval)
    pendingSchoolUpdateRequestsInterval = null
  }
  if (pendingTransferOrderGenerationInterval) {
    clearInterval(pendingTransferOrderGenerationInterval)
    pendingTransferOrderGenerationInterval = null
  }
}

// Function to determine menu item category
const getMenuItemCategory = (item: any): string => {
  const label = item.label?.toLowerCase() || ''
  const path = item.path?.toLowerCase() || ''
  
  // Main/Dashboard - check first
  if (label.includes('dashboard') || path === '/') {
    return 'Main'
  }
  
  // Transfer Management - check before Approvals to catch transfer-related approvals
  if (
    label.includes('transfer') ||
    label.includes('workflow') ||
    label.includes('approver assignment') ||
    label.includes('order management') ||
    path.includes('/transfer')
  ) {
    return 'Transfer Management'
  }
  
  // Health & welfare — medical records (e.g. teacher self-service)
  if (
    label.includes('medical record') ||
    path.includes('/medical-records')
  ) {
    return 'Health & welfare'
  }

  // Core Data — schools, teachers, seniority, etc.
  if (
    // Keep "School update requests" under Approvals (not Core Data)
    !path.includes('/school-update-requests') &&
    (label.includes('school') ||
      label.includes('teacher') ||
      label.includes('seniority') ||
      path.includes('/schools') ||
      path.includes('/teachers'))
  ) {
    return 'Core Data'
  }
  
  // Master Data
  if (
    label.includes('master data') ||
    label.includes('district') ||
    label.includes('subject') ||
    label.includes('religion') ||
    label.includes('management type') ||
    label.includes('school type') ||
    label.includes('medium') ||
    label.includes('block office') ||
    label.includes('village') ||
    label.includes('service categor') ||
    path.includes('/master-data')
  ) {
    return 'Master Data'
  }
  
  // Reports & Analytics
  if (
    (label.includes('report') && !label.includes('transfer')) ||
    label.includes('analytics') ||
    (path.includes('/report') && !path.includes('/transfer'))
  ) {
    return 'Reports & Analytics'
  }
  
  // Approvals - badge approvals, MLA approvals, etc. (but not transfer approvals)
  if (
    (label.includes('approval') && !label.includes('transfer')) ||
    label.includes('badge') ||
    label.includes('mla') ||
    path.includes('/education-qualifications-review') ||
    path.includes('/profile-approvals') ||
    path.includes('/phone-change-requests') ||
    path.includes('/school-update-requests')
  ) {
    return 'Approvals'
  }
  
  // Administration
  if (
    label.includes('user management') ||
    label.includes('audit') ||
    label.includes('menu management') ||
    label.includes('settings') ||
    path.includes('/user-management') ||
    path.includes('/audit') ||
    path.includes('/menu-management')
  ) {
    return 'Administration'
  }

  // Profile & services — own record, postings, transfer tracking, grievances, joining
  if (
    path.includes('/posting-history') ||
    path.includes('/teacher-profile') ||
    path.includes('/track-status') ||
    path.includes('/grievances') ||
    path.includes('/joining-reports') ||
    label.includes('grievance') ||
    label.includes('joining')
  ) {
    return 'Profile & services'
  }

  // Default category for uncategorized items
  return 'Other'
}

// Categorized menu items
const categorizedMenuItems = computed(() => {
  const categories: Record<string, { name: string; items: any[] }> = {
    'Main': { name: 'Main', items: [] },
    'Core Data': { name: 'Core Data', items: [] },
    'Health & welfare': { name: 'Health & welfare', items: [] },
    'Transfer Management': { name: 'Transfer Management', items: [] },
    'Master Data': { name: 'Master Data', items: [] },
    'Reports & Analytics': { name: 'Reports & Analytics', items: [] },
    'Approvals': { name: 'Approvals', items: [] },
    'Administration': { name: 'Administration', items: [] },
    'Profile & services': { name: 'Profile & services', items: [] },
    'Other': { name: 'Other', items: [] }
  }
  
  // Group menu items by category
  filteredMenuItems.value.forEach(item => {
    const category = getMenuItemCategory(item)
    if (categories[category]) {
      categories[category].items.push(item)
    } else {
      categories['Other'].items.push(item)
    }
  })
  
  // Return only categories that have items, maintaining order
  return [
    categories['Main'],
    categories['Core Data'],
    categories['Health & welfare'],
    categories['Transfer Management'],
    categories['Master Data'],
    categories['Reports & Analytics'],
    categories['Approvals'],
    categories['Administration'],
    categories['Profile & services'],
    categories['Other']
  ].filter(cat => cat.items.length > 0)
})

// Icon paths mapping
const iconPaths: Record<string, string> = {
  dashboard: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z',
  schools: 'M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z M12 14l9-5-9-5-9 5 9 5z',
  teachers: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  medical: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  'medical-records': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  reports: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z',
  audit: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  
  // Master Data icons
  'master-data': 'M4 7v10a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2z M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z M9 9h6M9 13h6M9 17h4',
  
  // Transfer Management icons
  'transfer order management': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  'workflow management': 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01 M13 10l-2 2 2 2m6-2l-2 2 2 2',
  'approver assignment': 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  // Assignment icon (alternative key)
  'assignment': 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  'transfer reports': 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  'transfer application approval': 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  // Approval icon (alternative key)
  'approval': 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  // Seniority List icon - ranking/list icon
  'seniority-list': 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M7 9h10M7 13h10M7 17h4',
  'minister oversight': 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  'minister-oversight': 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  'retirement-forecast': 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z M12 8v4l3 3',
  'vacancy-projection': 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  'district-shortage': 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z M12 8v4m0 4h.01',
  'recruitment-suggestions': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  'mla badge approval': 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  // Badge approval icon (alternative key)
  'badge-approval': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  
  // Menu Management icon - settings/gear icon
  'menu-management': 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  
  // Fallback icon for generic menu items - simple hamburger menu
  'menu': 'M4 6h16M4 12h16M4 18h16',
  // Master Data specific icons
  districts: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  subjects: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  religions: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  'management-types': 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  'school-types': 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  mediums: 'M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h3a1 1 0 011 1v1a1 1 0 01-1 1h-1v12a2 2 0 01-2 2H7a2 2 0 01-2-2V7H4a1 1 0 01-1-1V5a1 1 0 011-1h3zM9 7v10h6V7H9z',
  'block-offices': 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  villages: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  'service-categories': 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
  // Transfer and posting related icons
  'transfer-application': 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  'manual-transfer': 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z M8 7h8m-8 0V5a2 2 0 012-2h4a2 2 0 012 2v2m-8 0h8m-8 0l2 2m6-2l-2 2m0 0v8m0-8h4a2 2 0 012 2v6a2 2 0 01-2 2h-4a2 2 0 01-2-2v-6a2 2 0 012-2z',
  'posting-history': 'M3 21h18M5 21V7l7-4 7 4v14M9 9h2M9 13h2M9 17h2M13 9h2M13 13h2M13 17h2M7 21V9.5M17 21V9.5',
  'teachers-posting-histories': 'M3 21h18M5 21V7l7-4 7 4v14M9 9h2M9 13h2M9 17h2M13 9h2M13 13h2M13 17h2M7 21V9.5M17 21V9.5',
  // Teacher-specific icons
  'my-profile': 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  'track-status': 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  // Transfer Application sub-menu icons
  'apply-transfer': 'M12 6v6m0 0v6m0-6h6m-6 0H6',
  'pending-status': 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  'processing-status': 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11.356 2A8.003 8.003 0 0119 13a8.002 8.002 0 01-3.356-2M15 13H9m0 0v5a2 2 0 002 2h2a2 2 0 002-2v-5',
  'approved-status': 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  'rejected-status': 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
  // Workflow Management icon - flow chart/process icon
  'workflow-management': 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01 M13 10l-2 2 2 2m6-2l-2 2 2 2',
  // Workflow icon (alternative key)
  'workflow': 'M13 10V3L4 14h7v7l9-11h-7z',
  // Transfer Management icons
  'application-approval': 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  'transfer-reports': 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  // Additional contextual icons
  'transfer-management': 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  'approver-assignment': 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  'transfer-order': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  'advanced-transfer-order': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  // MLA Badge Approvals icon - shield badge
  'mla-badge-approval': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  // Priority Badge Approvals icon - star badge
  'priority-badge-approval': 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  // Grievance icon
  grievance: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
  // Joining Reports icon
  'joining-reports': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  // Aging Analytics icon
  analytics:
    'M3 3v18h18M7 14v4m4-10v10m4-6v6m4-12v12',
  // Hard Delete Requests (super_admin) - trash can / permanent delete
  'hard-delete-requests': 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  // Staff ID Cards - ID card icon
  'id-card': 'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 012-2h4a2 2 0 012 2v1m-4 0h4m-6 4h.01M9 12h.01M9 16h.01M12 12h3M12 16h3',
  // Generate ID Card - magic wand/sparkles icon (for generating/creating)
  'generate-id-card': 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
  // Teacher education qualifications (review queue)
  'education-qualifications':
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253 M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  // Profile update requests — teacher + verified check
  'profile-approvals':
    'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z M17 10.5l2.5 2.5 4-4.5',
  // Phone number change requests
  'phone-change-requests':
    'M3 5a2 2 0 012-2h3.279a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.128a11.042 11.042 0 005.516 5.516l1.128-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
}

/** Resolve icon key for menu item (path-based fallback so Hard Delete Requests always shows trash icon). */
const resolveMenuIcon = (item: { path?: string | null; icon?: string | null }) => {
  const normalizedPath = normalizeMenuPath(item.path)
  if (normalizedPath === '/hard-delete-requests') return 'hard-delete-requests'
  if (normalizedPath === '/education-qualifications-review') return 'education-qualifications'
  if (normalizedPath === '/profile-approvals') return 'profile-approvals'
  if (normalizedPath === '/phone-change-requests') return 'phone-change-requests'
  if (normalizedPath === '/aging-analytics') return 'analytics'
  return item.icon ?? undefined
}

const getIconPath = (icon?: string) => {
  if (!icon) return iconPaths.menu
  return iconPaths[icon] || iconPaths.menu
}

/** Teacher vs admin posting history use different paths but the same screen — highlight either menu link when on either route. */
const POSTING_HISTORY_ROUTE_PATHS = new Set(['/posting-history', '/posting-histories'])

const isActiveRoute = (path: string | null) => {
  if (!path || !path.trim()) return false
  
  if (path === '/') {
    return route.path === '/'
  }

  const normalizedMenuPath = normalizeMenuPath(path)
  if (
    normalizedMenuPath &&
    POSTING_HISTORY_ROUTE_PATHS.has(normalizedMenuPath) &&
    POSTING_HISTORY_ROUTE_PATHS.has(route.path)
  ) {
    return true
  }
  
  // Check for exact match first
  if (route.path === path) {
    return true
  }
  
  // For parent paths, only mark as active if:
  // 1. The route starts with the path AND
  // 2. There's no other menu item with a more specific path that matches exactly
  if (route.path.startsWith(path)) {
    // Check if there's a more specific menu item that matches exactly
    const allMenuPaths = getAllMenuPaths(filteredMenuItems.value)
    const hasMoreSpecificMatch = allMenuPaths.some(menuPath => 
      menuPath !== path && 
      menuPath.startsWith(path) && 
      route.path === menuPath
    )
    
    // Only mark as active if there's no more specific match
    return !hasMoreSpecificMatch
  }
  
  return false
}

// Helper function to normalize and validate menu paths
const normalizeMenuPath = (path: string | null | undefined): string | null => {
  if (!path || !path.trim()) {
    return null
  }
  
  let normalizedPath = path.trim()
  
  // Ensure path starts with '/' (absolute path)
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath
  }
  
  // List of known invalid paths that should be blocked
  // These are paths that don't exist in the router configuration
  const invalidPaths = [
    '/schools/posting-histories',
    '/schools/posting-history'
  ]
  
  // Check if path is in the invalid list (exact match)
  if (invalidPaths.includes(normalizedPath)) {
    logger.warn(`Invalid menu path detected: ${path}, returning null`)
    return null
  }

  // Remove trailing slash (except root) so "/foo/" matches "/foo"
  if (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath.replace(/\/+$/, '')
  }
  
  return normalizedPath
}

// Helper function to get all menu paths (including children)
const getAllMenuPaths = (items: any[]): string[] => {
  const paths: string[] = []
  items.forEach(item => {
    if (item.path) {
      const normalized = normalizeMenuPath(item.path)
      if (normalized) {
        paths.push(normalized)
      }
    }
    if (item.children && item.children.length > 0) {
      paths.push(...getAllMenuPaths(item.children))
    }
  })
  return paths
}

const toggleExpanded = (itemId: number) => {
  if (expandedItems.value.has(itemId)) {
    expandedItems.value.delete(itemId)
  } else {
    expandedItems.value.add(itemId)
  }
}

const handleMenuItemClick = (item: any) => {
  if (item.children && item.children.length > 0) {
    // If it has children, toggle expansion
    toggleExpanded(item.id)
  } else if (item.path && item.path.trim()) {
    // Normalize and validate path
    const normalizedPath = normalizeMenuPath(item.path)
    
    if (!normalizedPath) {
      // Invalid path, skip navigation
      return
    }
    
    // If it's a direct link, navigate to it
    try {
      router.push(normalizedPath)
      // Ensure parent items stay expanded when navigating
      ensureParentExpanded(item)
    } catch (error) {
      logger.error(`Failed to navigate to ${normalizedPath}:`, error)
    }
  }
}

const ensureParentExpanded = (item: any) => {
  // If this item has a parent_id, find and expand the parent
  if (item.parent_id) {
    const findParent = (items: any[], parentId: number): any => {
      for (const menuItem of items) {
        if (menuItem.id === parentId) {
          return menuItem
        }
        if (menuItem.children && menuItem.children.length > 0) {
          const found = findParent(menuItem.children, parentId)
          if (found) return found
        }
      }
      return null
    }
    
    const parent = findParent(filteredMenuItems.value, item.parent_id)
    if (parent) {
      expandedItems.value.add(parent.id)
      // Recursively expand grandparents if needed
      if (parent.parent_id) {
        ensureParentExpanded(parent)
      }
    }
  }
  
  // Also check if current route matches any child path and expand parent
  const currentPath = route.path
  const findParentForPath = (items: any[]): number | null => {
    for (const menuItem of items) {
      if (menuItem.children && menuItem.children.length > 0) {
        const matchingChild = menuItem.children.find((child: any) => 
          child.path && (currentPath === child.path || currentPath.startsWith(child.path))
        )
        if (matchingChild) {
          return menuItem.id
        }
        const nestedParent = findParentForPath(menuItem.children)
        if (nestedParent) {
          return menuItem.id
        }
      }
    }
    return null
  }
  
  const parentId = findParentForPath(filteredMenuItems.value)
  if (parentId) {
    expandedItems.value.add(parentId)
  }
}

const isExpanded = (itemId: number) => {
  return expandedItems.value.has(itemId)
}

// Check if menu item is Transfer Application
const isTransferApplicationItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = item.path?.toLowerCase() || ''
  // More flexible matching - check label OR path (not both required)
  const labelMatch = label.includes('transfer application') || 
                     label.includes('transfer application approval') ||
                     label.includes('transfer applications')
  const pathMatch = path.includes('/transfer-application') || 
                   path.includes('transfer-application') || 
                   path.includes('/transfer-application-approval') ||
                   path.includes('transfer-application-approval')
  
  const isMatch = labelMatch || pathMatch
  if (isMatch) {
    logger.debug('Matched Transfer Application menu item:', { 
      label: item.label, 
      path: item.path, 
      pendingCount: pendingTransferCount.value 
    })
  }
  return isMatch
}

// Check if menu item is Joining Reports
const isJoiningReportsItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = item.path?.toLowerCase() || ''
  return label.includes('joining report') && (path.includes('/joining-reports') || path.includes('/joining-report'))
}

// Check if menu item is Priority Badge Approval
const isPriorityBadgeApprovalItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = item.path?.toLowerCase() || ''
  return label.includes('priority badge approval') || label.includes('priority badge') && path.includes('/priority-badge')
}

// Check if menu item is Grievances
const isGrievancesItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = item.path?.toLowerCase() || ''
  return label.includes('grievance') && path.includes('/grievance')
}

// Check if menu item is Hard Delete Requests
const isHardDeleteRequestsItem = (item: any): boolean => {
  const path = item.path?.toLowerCase() || ''
  return path.includes('/hard-delete-requests')
}

const isEducationQualReviewMenuItem = (item: any): boolean => {
  const path = (item.path || '').toLowerCase()
  return path === '/education-qualifications-review' || path.includes('/education-qualifications-review')
}

const isMedicalRecordsMenuItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = (item.path || '').toLowerCase()
  return path.includes('/medical-records') || label.includes('medical record')
}

const isProfileApprovalsMenuItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = (item.path || '').toLowerCase()
  return path === '/profile-approvals' || path.includes('/profile-approvals') || label.includes('profile approval')
}

const isPhoneChangeRequestsMenuItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = (item.path || '').toLowerCase()
  return path === '/phone-change-requests' || path.includes('/phone-change-requests') || label.includes('phone change request')
}

const isSchoolUpdateRequestsMenuItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = (item.path || '').toLowerCase()
  return path === '/school-update-requests' || path.includes('/school-update-requests') || label.includes('school update request')
}

const isTransferReportsMenuItem = (item: any): boolean => {
  const label = item.label?.toLowerCase() || ''
  const path = (item.path || '').toLowerCase()
  return label.includes('transfer report') || path === '/transfer-reports' || path.includes('/transfer-reports')
}

const getMenuItemBadgeCount = (item: any): number | null => {
  if (isJoiningReportsItem(item) && pendingJoiningReportsCount.value > 0) {
    return pendingJoiningReportsCount.value
  }
  if (isPriorityBadgeApprovalItem(item) && pendingPriorityBadgeCount.value > 0) {
    return pendingPriorityBadgeCount.value
  }
  if (isHardDeleteRequestsItem(item) && pendingHardDeleteCount.value > 0) {
    return pendingHardDeleteCount.value
  }
  if (isTransferApplicationItem(item) && pendingTransferCount.value > 0) {
    return pendingTransferCount.value
  }
  if (isMedicalRecordsMenuItem(item) && pendingMedicalRecordsCount.value > 0) {
    return pendingMedicalRecordsCount.value
  }
  if (isEducationQualReviewMenuItem(item) && pendingEducationQualReviewCount.value > 0) {
    return pendingEducationQualReviewCount.value
  }
  if (isProfileApprovalsMenuItem(item) && pendingProfileApprovalsCount.value > 0) {
    return pendingProfileApprovalsCount.value
  }
  if (isPhoneChangeRequestsMenuItem(item) && pendingPhoneChangeRequestsCount.value > 0) {
    return pendingPhoneChangeRequestsCount.value
  }
  if (isSchoolUpdateRequestsMenuItem(item) && pendingSchoolUpdateRequestsCount.value > 0) {
    return pendingSchoolUpdateRequestsCount.value
  }
  if (isTransferReportsMenuItem(item) && pendingTransferOrderGenerationCount.value > 0) {
    return pendingTransferOrderGenerationCount.value
  }
  if (isGrievancesItem(item) && pendingGrievancesCount.value > 0) {
    return pendingGrievancesCount.value
  }
  return null
}

const getMenuItemBadgeTitle = (item: any): string => {
  if (isMedicalRecordsMenuItem(item)) return 'Medical record(s) pending review'
  if (isEducationQualReviewMenuItem(item)) return 'Qualifications pending review'
  if (isProfileApprovalsMenuItem(item)) return 'Profile update requests pending review'
  if (isPhoneChangeRequestsMenuItem(item)) return 'Phone change requests pending review'
  if (isSchoolUpdateRequestsMenuItem(item)) return 'School update requests pending review'
  if (isTransferReportsMenuItem(item)) return 'Approved applications pending transfer order generation'
  if (isGrievancesItem(item)) return 'New grievance submissions pending'
  if (isJoiningReportsItem(item)) {
    return currentUser.value?.role?.toLowerCase() === 'teacher'
      ? 'Transfer order issued — submit your joining report'
      : 'Joining reports pending verification'
  }
  if (isPriorityBadgeApprovalItem(item)) return 'Priority badges pending approval'
  if (isHardDeleteRequestsItem(item)) return 'Hard delete requests pending'
  if (isTransferApplicationItem(item)) return 'Transfer applications pending your attention'
  return ''
}

// Load pending transfer applications count
const loadPendingTransferCount = async () => {
  try {
    // Only load count for users who can see transfer applications
    const userRole = currentUser.value?.role?.toLowerCase() || ''
    const rolesThatCanSeeTransfers = ['super_admin', 'minister', 'admin_directorate', 'admin_secretariat', 'assistant', 'superintendent', 'deputy_director', 'joint_director', 'under_secretary', 'deputy_secretary', 'joint_secretary', 'additional_secretary', 'commissioner', 'deo', 'sdeo', 'hoi', 'teacher']
    
    logger.debug('Loading pending transfer count for role:', userRole)
    
    if (!rolesThatCanSeeTransfers.includes(userRole)) {
      logger.debug('Role not in allowed list, setting count to 0')
      pendingTransferCount.value = 0
      return
    }

    const response =
      userRole === 'teacher'
        ? await transferApplicationsApi.getTeacherNotificationCount()
        : await transferApplicationsApi.getPendingApprovalCount()
    logger.debug('Pending transfer count API response:', response.data)

    if (response.data.success) {
      if (userRole === 'teacher') {
        const count = (response.data as any).data?.count
        pendingTransferCount.value = typeof count === 'number' ? count : 0
      } else {
        const count = (response.data as any).data?.count
        pendingTransferCount.value = typeof count === 'number' ? count : 0
      }
      logger.debug('Set pendingTransferCount to:', pendingTransferCount.value)
    } else {
      logger.debug('API response not successful, setting count to 0')
      pendingTransferCount.value = 0
    }
  } catch (error: any) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingTransferCount.value = 0
      return
    }
    logger.error('Failed to load pending transfer count:', error)
    logger.error('Error details:', error.response?.data || error.message)
    pendingTransferCount.value = 0
  }
}

// Load pending joining reports count
const loadPendingJoiningReportsCount = async () => {
  try {
    // Only load count for users who can see joining reports
    const userRole = currentUser.value?.role?.toLowerCase() || ''
    const rolesThatCanSeeJoiningReports = ['super_admin', 'minister', 'admin_directorate', 'admin_secretariat', 'assistant', 'superintendent', 'deputy_director', 'joint_director', 'under_secretary', 'deputy_secretary', 'joint_secretary', 'additional_secretary', 'commissioner', 'deo', 'sdeo', 'hoi', 'teacher']
    
    if (!rolesThatCanSeeJoiningReports.includes(userRole)) {
      pendingJoiningReportsCount.value = 0
      return
    }

    const response = await joiningReportsApi.getSidebarBadgeCount()
    if (response.data.success && response.data.pagination) {
      pendingJoiningReportsCount.value = response.data.pagination.total || 0
    } else {
      pendingJoiningReportsCount.value = 0
    }
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingJoiningReportsCount.value = 0
      return
    }
    logger.debug('Failed to load pending joining reports count:', error)
    pendingJoiningReportsCount.value = 0
  }
}

// Load pending priority badge approvals count
const loadPendingPriorityBadgeCount = async () => {
  try {
    // Only load count for admin users who can approve priority badges
    const userRole = currentUser.value?.role?.toLowerCase() || ''
    const rolesThatCanApproveBadges = ['super_admin', 'admin_directorate', 'admin_secretariat', 'assistant', 'superintendent', 'deputy_director', 'joint_director', 'under_secretary', 'deputy_secretary', 'joint_secretary', 'additional_secretary', 'commissioner']
    
    if (!rolesThatCanApproveBadges.includes(userRole)) {
      pendingPriorityBadgeCount.value = 0
      return
    }

    const response = await priorityBadgesApi.getPendingCount()
    if (response.data.success && response.data.pagination) {
      pendingPriorityBadgeCount.value = response.data.pagination.total || 0
    } else {
      pendingPriorityBadgeCount.value = 0
    }
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingPriorityBadgeCount.value = 0
      return
    }
    logger.debug('Failed to load pending priority badge count:', error)
    pendingPriorityBadgeCount.value = 0
  }
}

// Load pending grievances count
const loadPendingGrievancesCount = async () => {
  try {
    // Only load count for admin users who can view grievances
    const userRole = currentUser.value?.role?.toLowerCase() || ''
    const rolesThatCanViewGrievances = ['super_admin', 'minister', 'admin_directorate', 'admin_secretariat', 'assistant', 'superintendent', 'deputy_director', 'joint_director', 'under_secretary', 'deputy_secretary', 'joint_secretary', 'additional_secretary', 'commissioner', 'deo', 'sdeo', 'hoi']
    
    if (!rolesThatCanViewGrievances.includes(userRole)) {
      pendingGrievancesCount.value = 0
      return
    }

    // Backend automatically determines recipient_type based on user role
    const response = await grievancesApi.getPendingCount()
    if (response.data.success && response.data.pagination) {
      pendingGrievancesCount.value = response.data.pagination.total || 0
    } else {
      pendingGrievancesCount.value = 0
    }
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingGrievancesCount.value = 0
      return
    }
    logger.debug('Failed to load pending grievances count:', error)
    pendingGrievancesCount.value = 0
  }
}

// Load pending hard delete requests count (super_admin only)
const loadPendingHardDeleteCount = async () => {
  try {
    const userRole = currentUser.value?.role?.toLowerCase() || ''
    if (userRole !== 'super_admin') {
      pendingHardDeleteCount.value = 0
      return
    }

    const response = await hardDeleteRequestsApi.list({ status: 'pending', limit: 1 })
    if (response.data.success && typeof response.data.total === 'number') {
      pendingHardDeleteCount.value = response.data.total
    } else {
      pendingHardDeleteCount.value = 0
    }
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingHardDeleteCount.value = 0
      return
    }
    logger.debug('Failed to load pending hard delete count:', error)
    pendingHardDeleteCount.value = 0
  }
}

const loadPendingMedicalRecordsCount = async () => {
  try {
    const n = await fetchPendingMedicalRecordsCountForRole(currentUser.value?.role)
    pendingMedicalRecordsCount.value = n
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingMedicalRecordsCount.value = 0
      return
    }
    logger.debug('Failed to load pending medical records count:', error)
    pendingMedicalRecordsCount.value = 0
  }
}

const loadPendingEducationQualReviewCount = async () => {
  try {
    if (!canApproveEducationQualificationRole(currentUser.value?.role)) {
      pendingEducationQualReviewCount.value = 0
      return
    }
    const response = await simpleProfileApi.getAdminEducationQualPendingCount()
    if (response.data?.success && response.data.data && typeof response.data.data.count === 'number') {
      pendingEducationQualReviewCount.value = response.data.data.count
    } else {
      pendingEducationQualReviewCount.value = 0
    }
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingEducationQualReviewCount.value = 0
      return
    }
    logger.debug('Failed to load pending education qualification review count:', error)
    pendingEducationQualReviewCount.value = 0
  }
}

const loadPendingProfileApprovalsCount = async () => {
  try {
    const role = currentUser.value?.role
    const roleLower = role?.toLowerCase() || ''
    const allowedRoles = [
      'super_admin', 'admin', 'admin_directorate', 'admin_secretariat',
      'assistant', 'superintendent', 'deputy_director', 'joint_director',
      'under_secretary', 'deputy_secretary', 'joint_secretary', 'additional_secretary',
      'commissioner', 'deo', 'sdeo', 'hoi', 'minister'
    ]

    if (!allowedRoles.includes(roleLower) && !isBranchAdminRole(role)) {
      pendingProfileApprovalsCount.value = 0
      return
    }

    let profileUpdateCount = 0
    let postingHistoryCount = 0

    if (isBranchAdminRole(role) || roleLower === 'super_admin' || roleLower === 'admin') {
      try {
        const resp = await simpleProfileApi.adminListProfileUpdateRequestsPending({ page: 1, limit: 1 })
        const data = resp.data?.data ?? resp.data?.data?.data ?? resp.data?.data
        profileUpdateCount = Number((data?.total ?? data?.pagination?.total ?? 0) || 0)
      } catch (e) {
        logger.debug('Failed to load profile update requests count:', e)
      }
    }

    try {
      const postingResp = await postingHistoryApi.getPendingPostingHistoryRequests({ page: 1, limit: 1 })
      if (postingResp.data?.success) {
        const pData = postingResp.data.data
        if (pData) {
          postingHistoryCount = Number((pData.total ?? pData.pagination?.total ?? (Array.isArray(pData.rows) ? pData.rows.length : 0)) || 0)
        }
      }
    } catch (e) {
      logger.debug('Failed to load pending posting history count:', e)
    }

    pendingProfileApprovalsCount.value = profileUpdateCount + postingHistoryCount
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingProfileApprovalsCount.value = 0
      return
    }
    logger.debug('Failed to load pending profile approvals count:', error)
    pendingProfileApprovalsCount.value = 0
  }
}

const loadPendingPhoneChangeRequestsCount = async () => {
  try {
    const role = currentUser.value?.role
    if (!(isBranchAdminRole(role) || role?.toLowerCase() === 'super_admin' || role?.toLowerCase() === 'admin')) {
      pendingPhoneChangeRequestsCount.value = 0
      return
    }
    const resp = await simpleProfileApi.adminPendingPhoneChangeCount()
    const count = (resp.data as any)?.data?.count
    pendingPhoneChangeRequestsCount.value = typeof count === 'number' ? count : 0
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingPhoneChangeRequestsCount.value = 0
      return
    }
    logger.debug('Failed to load pending phone change requests count:', error)
    pendingPhoneChangeRequestsCount.value = 0
  }
}

const loadPendingSchoolUpdateRequestsCount = async () => {
  try {
    const role = currentUser.value?.role
    if (!(isBranchAdminRole(role) || role?.toLowerCase() === 'super_admin' || role?.toLowerCase() === 'admin')) {
      pendingSchoolUpdateRequestsCount.value = 0
      return
    }
    const resp = await schoolsApi.adminPendingUpdateRequestsCount()
    const count = (resp.data as any)?.data?.count
    pendingSchoolUpdateRequestsCount.value = typeof count === 'number' ? count : 0
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingSchoolUpdateRequestsCount.value = 0
      return
    }
    logger.debug('Failed to load pending school update requests count:', error)
    pendingSchoolUpdateRequestsCount.value = 0
  }
}

const loadPendingTransferOrderGenerationCount = async () => {
  try {
    const role = (currentUser.value?.role || '').toLowerCase()
    const rolesThatGenerateOrders = ['super_admin', 'admin_directorate', 'admin_secretariat']
    if (!rolesThatGenerateOrders.includes(role)) {
      pendingTransferOrderGenerationCount.value = 0
      return
    }
    const resp = await transferOrdersApi.getApprovedApplicationsWithoutOrders(1, 1)
    const payload = resp.data as any
    const total = Number(
      payload?.pagination?.totalCount ??
      payload?.data?.pagination?.totalCount ??
      payload?.pagination?.total ??
      payload?.data?.pagination?.total ??
      payload?.totalCount ??
      0
    )
    pendingTransferOrderGenerationCount.value = Number.isFinite(total) ? total : 0
  } catch (error) {
    if (isUnauthorized(error)) {
      stopSidebarPolling()
      pendingTransferOrderGenerationCount.value = 0
      return
    }
    logger.debug('Failed to load pending transfer-order generation count:', error)
    pendingTransferOrderGenerationCount.value = 0
  }
}

const handleLogout = async () => {
  await auth.logout()
  router.push('/login')
}

const goToProfile = () => {
  if (currentUser.value?.role === 'teacher') {
    router.push('/teacher-profile')
    return
  }
  router.push('/account-profile')
}

const loadProfilePicture = async () => {
  try {
    // Only load profile picture for teachers
    if (currentUser.value?.role !== 'teacher') {
      userProfilePicture.value = null
      return
    }

    const user = currentUser.value
    if (!user?.id) {
      return
    }

    // Try to get teacher ID - first try by phone, then by user ID
    let teacherId: number | null = null

    try {
      const resp = await api.get('/transfer-applications/teacher-current-school')
      const t = resp.data?.data?.teacher
      if (t?.id) teacherId = Number(t.id) || null
    } catch (error) {
      logger.debug('Failed to get teacher from teacher-current-school, trying by ID:', error)
    }

    // Fallback: try using user ID as teacher ID
    if (!teacherId && user.id) {
      try {
        const teacherResponse = await teachersApi.getById(user.id)
        if (teacherResponse.data.success && teacherResponse.data.data?.id) {
          teacherId = teacherResponse.data.data.id
        }
      } catch (error) {
        logger.debug('Failed to get teacher by ID:', error)
      }
    }

    // Load profile picture if we have a teacher ID
    if (teacherId) {
      const response = await simpleProfileApi.getProfilePicture(teacherId)
      if (response.data.success && response.data.data) {
        userProfilePicture.value = response.data.data.url || response.data.data.file_path || null
        logger.debug('✅ Profile picture loaded for sidebar:', userProfilePicture.value)
      } else {
        userProfilePicture.value = null
      }
    } else {
      userProfilePicture.value = null
    }
  } catch (error) {
    logger.debug('Failed to load profile picture for sidebar:', error)
    userProfilePicture.value = null
  }
}

const getProfilePictureUrl = (url: string) => {
  if (!url) return ''
  
  let pathToUse = url
  const urlWithoutParams = url.split('?')[0]
  
  if (urlWithoutParams.startsWith('http://localhost:5004')) {
    pathToUse = urlWithoutParams.replace('http://localhost:5004', '')
  } else if (urlWithoutParams.startsWith('http://') || urlWithoutParams.startsWith('https://')) {
    try {
      const urlObj = new URL(urlWithoutParams)
      pathToUse = urlObj.pathname
    } catch {
      pathToUse = urlWithoutParams
    }
  } else if (!urlWithoutParams.startsWith('/')) {
    pathToUse = '/' + urlWithoutParams
  } else {
    pathToUse = urlWithoutParams
  }
  
  const separator = pathToUse.includes('?') ? '&' : '?'
  return `${pathToUse}${separator}t=${Date.now()}`
}

const handleImageError = () => {
  logger.debug('Profile picture failed to load in sidebar, removing')
  userProfilePicture.value = null
}

async function validateSessionOrClear(): Promise<boolean> {
  try {
    // IMPORTANT: Teachers authenticate via OTP and are not present in `users`,
    // so `/api/users/me` can return 401 even when teacher cookies are valid.
    // Use token-status (works for all roles, even when access token is expired).
    const resp = await fetch('/api/sessions/token-status', { credentials: 'include' })
    if (!resp.ok) {
      auth.clearAuthState()
      stopSidebarPolling()
      return false
    }
    const body = await resp.json().catch(() => null)
    const expired = body?.data?.tokenExpiration?.isExpired === true
    if (expired) {
      auth.clearAuthState()
      stopSidebarPolling()
      return false
    }
    return true
  } catch {
    auth.clearAuthState()
    stopSidebarPolling()
    return false
  }
}

const loadAllBadgeCounts = async () => {
  await Promise.allSettled([
    loadPendingTransferCount(),
    loadPendingJoiningReportsCount(),
    loadPendingPriorityBadgeCount(),
    loadPendingGrievancesCount(),
    loadPendingHardDeleteCount(),
    loadPendingMedicalRecordsCount(),
    loadPendingEducationQualReviewCount(),
    loadPendingProfileApprovalsCount(),
    loadPendingPhoneChangeRequestsCount(),
    loadPendingSchoolUpdateRequestsCount(),
    loadPendingTransferOrderGenerationCount(),
  ])
}

onMounted(async () => {
  // Only load menu items if user is authenticated (local state) AND session is valid (server).
  logger.debug('Sidebar mounted', { isLoggedIn: auth.isLoggedIn, currentUser: currentUser.value })
  if (auth.isLoggedIn && currentUser.value) {
    const ok = await validateSessionOrClear()
    if (!ok) return

    logger.debug('Loading menu items on mount')
    loadMenuItems()
    loadProfilePicture()
    loadAllBadgeCounts()
    
    // Set up single consolidated interval to refresh pending counts every 30 seconds
    pendingCountInterval = setInterval(() => {
      loadAllBadgeCounts()
    }, 30000)
    
    // Listen for custom events to refresh counts when actions occur
    const handleTransferApplicationChange = () => {
      logger.debug('Transfer application changed, refreshing count')
      loadPendingTransferCount()
      loadPendingJoiningReportsCount()
      loadPendingTransferOrderGenerationCount()
    }
    
    window.addEventListener('transfer-application-changed', handleTransferApplicationChange)
    window.addEventListener('transfer-application-pullback', handleTransferApplicationChange)
    window.addEventListener('transfer-application-rejected', handleTransferApplicationChange)
    window.addEventListener('transfer-application-approved', handleTransferApplicationChange)
    
    const handleHardDeleteRequestChange = () => {
      loadPendingHardDeleteCount()
    }
    window.addEventListener('hard-delete-request-changed', handleHardDeleteRequestChange)

    const handleMedicalRecordsChanged = () => {
      loadPendingMedicalRecordsCount()
    }
    window.addEventListener(MEDICAL_RECORDS_CHANGED_EVENT, handleMedicalRecordsChanged)

    const handleEducationQualificationsChanged = () => {
      loadPendingEducationQualReviewCount()
    }
    window.addEventListener(EDUCATION_QUALIFICATIONS_CHANGED_EVENT, handleEducationQualificationsChanged)

    const handleProfileApprovalsChanged = () => {
      loadPendingProfileApprovalsCount()
    }
    window.addEventListener('profile-approvals-changed', handleProfileApprovalsChanged)
    
    // Store cleanup function
    ;(window as any).__sidebarCleanup = () => {
      window.removeEventListener('transfer-application-changed', handleTransferApplicationChange)
      window.removeEventListener('transfer-application-pullback', handleTransferApplicationChange)
      window.removeEventListener('transfer-application-rejected', handleTransferApplicationChange)
      window.removeEventListener('transfer-application-approved', handleTransferApplicationChange)
      window.removeEventListener('hard-delete-request-changed', handleHardDeleteRequestChange)
      window.removeEventListener(MEDICAL_RECORDS_CHANGED_EVENT, handleMedicalRecordsChanged)
      window.removeEventListener(EDUCATION_QUALIFICATIONS_CHANGED_EVENT, handleEducationQualificationsChanged)
      window.removeEventListener('profile-approvals-changed', handleProfileApprovalsChanged)
    }
    
    // Restore expanded state from localStorage after menu items are loaded
    try {
      const saved = localStorage.getItem('sidebar_expanded_items')
      if (saved) {
        const expandedArray = JSON.parse(saved)
        expandedItems.value = new Set(expandedArray)
      }
    } catch (error) {
      logger.debug('Failed to restore expanded items from localStorage:', error)
    }
    
    // Auto-expand parents for current route
    const currentPath = route.path
    if (currentPath.includes('/posting-history') || currentPath.includes('/posting-histories')) {
      // Wait for menu items to load, then expand parents
      setTimeout(() => {
        const expandParentsForPostingHistory = (items: any[]) => {
          items.forEach(item => {
            if (item.children && item.children.length > 0) {
              const hasPostingHistoryChild = item.children.some((child: any) => 
                child.path && (
                  child.path.includes('/posting-history') || 
                  child.path.includes('/posting-histories') ||
                  child.label?.toLowerCase().includes('posting history') ||
                  child.label?.toLowerCase().includes('teachers posting')
                )
              )
              if (hasPostingHistoryChild) {
                expandedItems.value.add(item.id)
              }
              expandParentsForPostingHistory(item.children)
            }
          })
        }
        expandParentsForPostingHistory(filteredMenuItems.value)
      }, 100)
    }
  }
})

onUnmounted(() => {
  // Clear intervals when component is unmounted
  if (pendingCountInterval) {
    clearInterval(pendingCountInterval)
    pendingCountInterval = null
  }
  if (joiningReportsCountInterval) {
    clearInterval(joiningReportsCountInterval)
    joiningReportsCountInterval = null
  }
  if (priorityBadgeCountInterval) {
    clearInterval(priorityBadgeCountInterval)
    priorityBadgeCountInterval = null
  }
  if (grievancesCountInterval) {
    clearInterval(grievancesCountInterval)
    grievancesCountInterval = null
  }
  if (hardDeleteCountInterval) {
    clearInterval(hardDeleteCountInterval)
    hardDeleteCountInterval = null
  }
  if (pendingMedicalRecordsInterval) {
    clearInterval(pendingMedicalRecordsInterval)
    pendingMedicalRecordsInterval = null
  }
  if (pendingEducationQualReviewInterval) {
    clearInterval(pendingEducationQualReviewInterval)
    pendingEducationQualReviewInterval = null
  }
  if (pendingProfileApprovalsInterval) {
    clearInterval(pendingProfileApprovalsInterval)
    pendingProfileApprovalsInterval = null
  }
  if (pendingPhoneChangeRequestsInterval) {
    clearInterval(pendingPhoneChangeRequestsInterval)
    pendingPhoneChangeRequestsInterval = null
  }
  if (pendingTransferOrderGenerationInterval) {
    clearInterval(pendingTransferOrderGenerationInterval)
    pendingTransferOrderGenerationInterval = null
  }
  
  // Clean up event listeners
  if ((window as any).__sidebarCleanup) {
    ;(window as any).__sidebarCleanup()
  }
})

// Reload menu items when token is refreshed
useTokenRefreshReload(() => {
  if (auth.isLoggedIn && currentUser.value) {
    loadMenuItems()
    loadPendingTransferCount()
    loadPendingJoiningReportsCount()
    loadPendingPriorityBadgeCount()
    loadPendingGrievancesCount()
    loadPendingHardDeleteCount()
    loadPendingMedicalRecordsCount()
    loadPendingEducationQualReviewCount()
    loadPendingProfileApprovalsCount()
    loadPendingPhoneChangeRequestsCount()
    loadPendingTransferOrderGenerationCount()
  }
})

// Watch for authentication changes
watch(() => auth.isAuthenticated, (isAuthenticated) => {
  if (isAuthenticated && currentUser.value) {
    loadMenuItems()
  }
})

// Watch for user data changes
watch(() => currentUser.value, (user) => {
  if (user && auth.isAuthenticated) {
    loadMenuItems()
    loadProfilePicture()
    loadPendingTransferCount()
    loadPendingJoiningReportsCount()
    loadPendingPriorityBadgeCount()
    loadPendingGrievancesCount()
    loadPendingHardDeleteCount()
    loadPendingMedicalRecordsCount()
    loadPendingEducationQualReviewCount()
    loadPendingProfileApprovalsCount()
    loadPendingPhoneChangeRequestsCount()
    loadPendingTransferOrderGenerationCount()
    
    // Restart intervals when user changes
    if (pendingCountInterval) {
      clearInterval(pendingCountInterval)
    }
    pendingCountInterval = setInterval(() => {
      loadPendingTransferCount()
    }, 30000)
    
    if (joiningReportsCountInterval) {
      clearInterval(joiningReportsCountInterval)
    }
    joiningReportsCountInterval = setInterval(() => {
      loadPendingJoiningReportsCount()
    }, 30000)
    
    if (priorityBadgeCountInterval) {
      clearInterval(priorityBadgeCountInterval)
    }
    priorityBadgeCountInterval = setInterval(() => {
      loadPendingPriorityBadgeCount()
    }, 30000)
    
    if (grievancesCountInterval) {
      clearInterval(grievancesCountInterval)
    }
    grievancesCountInterval = setInterval(() => {
      loadPendingGrievancesCount()
    }, 30000)
    
    if (user?.role === 'super_admin') {
      if (hardDeleteCountInterval) {
        clearInterval(hardDeleteCountInterval)
      }
      hardDeleteCountInterval = setInterval(() => {
        loadPendingHardDeleteCount()
      }, 30000)
    }

    if (pendingMedicalRecordsInterval) {
      clearInterval(pendingMedicalRecordsInterval)
    }
    pendingMedicalRecordsInterval = setInterval(() => {
      loadPendingMedicalRecordsCount()
    }, 30000)

    if (pendingEducationQualReviewInterval) {
      clearInterval(pendingEducationQualReviewInterval)
    }
    pendingEducationQualReviewInterval = setInterval(() => {
      loadPendingEducationQualReviewCount()
    }, 30000)

    if (pendingProfileApprovalsInterval) {
      clearInterval(pendingProfileApprovalsInterval)
    }
    pendingProfileApprovalsInterval = setInterval(() => {
      loadPendingProfileApprovalsCount()
    }, 30000)
    if (pendingPhoneChangeRequestsInterval) {
      clearInterval(pendingPhoneChangeRequestsInterval)
    }
    pendingPhoneChangeRequestsInterval = setInterval(() => {
      loadPendingPhoneChangeRequestsCount()
    }, 30000)
    if (pendingTransferOrderGenerationInterval) {
      clearInterval(pendingTransferOrderGenerationInterval)
    }
    pendingTransferOrderGenerationInterval = setInterval(() => {
      loadPendingTransferOrderGenerationCount()
    }, 30000)
  } else {
    userProfilePicture.value = null
    pendingTransferCount.value = 0
    pendingJoiningReportsCount.value = 0
    pendingPriorityBadgeCount.value = 0
    pendingGrievancesCount.value = 0
    pendingHardDeleteCount.value = 0
    pendingMedicalRecordsCount.value = 0
    pendingEducationQualReviewCount.value = 0
    pendingProfileApprovalsCount.value = 0
    pendingPhoneChangeRequestsCount.value = 0
    pendingTransferOrderGenerationCount.value = 0
    if (pendingEducationQualReviewInterval) {
      clearInterval(pendingEducationQualReviewInterval)
      pendingEducationQualReviewInterval = null
    }
    if (pendingProfileApprovalsInterval) {
      clearInterval(pendingProfileApprovalsInterval)
      pendingProfileApprovalsInterval = null
    }
    if (pendingPhoneChangeRequestsInterval) {
      clearInterval(pendingPhoneChangeRequestsInterval)
      pendingPhoneChangeRequestsInterval = null
    }
    if (pendingTransferOrderGenerationInterval) {
      clearInterval(pendingTransferOrderGenerationInterval)
      pendingTransferOrderGenerationInterval = null
    }
    if (pendingCountInterval) {
      clearInterval(pendingCountInterval)
      pendingCountInterval = null
    }
    if (joiningReportsCountInterval) {
      clearInterval(joiningReportsCountInterval)
      joiningReportsCountInterval = null
    }
    if (priorityBadgeCountInterval) {
      clearInterval(priorityBadgeCountInterval)
      priorityBadgeCountInterval = null
    }
    if (grievancesCountInterval) {
      clearInterval(grievancesCountInterval)
      grievancesCountInterval = null
    }
    if (hardDeleteCountInterval) {
      clearInterval(hardDeleteCountInterval)
      hardDeleteCountInterval = null
    }
    if (pendingMedicalRecordsInterval) {
      clearInterval(pendingMedicalRecordsInterval)
      pendingMedicalRecordsInterval = null
    }
  }
})

// Auto-expand parent menu items when navigating to child routes
watch(() => route.path, (newPath) => {
  // Refresh pending counts when navigating to relevant pages
  if (newPath.includes('/transfer-application')) {
    loadPendingTransferCount()
  }
  if (newPath.includes('/joining-report')) {
    loadPendingJoiningReportsCount()
  }
  if (newPath.includes('/priority-badge')) {
    loadPendingPriorityBadgeCount()
  }
  if (newPath.includes('/grievance')) {
    loadPendingGrievancesCount()
  }
  if (newPath.includes('/hard-delete-requests')) {
    loadPendingHardDeleteCount()
  }
  if (newPath.includes('/medical-records')) {
    loadPendingMedicalRecordsCount()
  }
  if (newPath.includes('/education-qualifications-review')) {
    loadPendingEducationQualReviewCount()
  }
  if (newPath.includes('/profile-approvals')) {
    loadPendingProfileApprovalsCount()
  }
  if (newPath.includes('/phone-change-requests')) {
    loadPendingPhoneChangeRequestsCount()
  }
  if (newPath.includes('/transfer-reports') || newPath.includes('/transfer-order')) {
    loadPendingTransferOrderGenerationCount()
  }
  
  // Find menu items that have children matching the current route
  const findParentForRoute = (items: any[]): number | null => {
    for (const item of items) {
      // Check if current route matches this item's path
      if (item.path && newPath.startsWith(item.path) && item.path !== '/') {
        return item.id
      }
      // Check if current route matches any child's path
      if (item.children && item.children.length > 0) {
        const matchingChild = item.children.find((child: any) => 
          child.path && (newPath === child.path || newPath.startsWith(child.path))
        )
        if (matchingChild) {
          return item.id
        }
        // Recursively check nested children
        const nestedParent = findParentForRoute(item.children)
        if (nestedParent) {
          return item.id
        }
      }
    }
    return null
  }
  
  const parentId = findParentForRoute(filteredMenuItems.value)
  if (parentId) {
    expandedItems.value.add(parentId)
  }
  
  // Ensure posting history routes keep their parent expanded
  if (newPath.includes('/posting-history') || newPath.includes('/posting-histories')) {
    // Find all parent items that contain posting history as a child
    const expandParentsForPostingHistory = (items: any[]) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          const hasPostingHistoryChild = item.children.some((child: any) => 
            child.path && (
              child.path.includes('/posting-history') || 
              child.path.includes('/posting-histories') ||
              child.label?.toLowerCase().includes('posting history') ||
              child.label?.toLowerCase().includes('teachers posting')
            )
          )
          if (hasPostingHistoryChild) {
            expandedItems.value.add(item.id)
          }
          // Recursively check nested children
          expandParentsForPostingHistory(item.children)
        }
      })
    }
    
    expandParentsForPostingHistory(filteredMenuItems.value)
  }
}, { immediate: true })

// Persist expanded state to localStorage
watch(expandedItems, (newSet) => {
  try {
    const expandedArray = Array.from(newSet)
    localStorage.setItem('sidebar_expanded_items', JSON.stringify(expandedArray))
  } catch (error) {
    logger.debug('Failed to save expanded items to localStorage:', error)
  }
}, { deep: true })

// Restore expanded state from localStorage on mount
onMounted(() => {
  try {
    const saved = localStorage.getItem('sidebar_expanded_items')
    if (saved) {
      const expandedArray = JSON.parse(saved)
      expandedItems.value = new Set(expandedArray)
    }
  } catch (error) {
    logger.debug('Failed to restore expanded items from localStorage:', error)
  }
})
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter-from {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 200px;
  transform: translateY(0);
}
</style>
